/* eslint-disable react/no-unstable-nested-components */
import React, { useContext, useEffect, useState } from "react";

// Third-party libraries
import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import Image from "next/legacy/image";
import { useParams, useSearchParams } from "../../hooks/useNavigation";
import PropTypes from "prop-types";
import { isMobile } from "react-device-detect";
import styled from "styled-components";

// Contexts
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
// Constants and helpers
import breakpoints from "../../constants";
import {
  getUTMFromParams,
  useAnalytics,
} from "../../helpers/AnalyticsTrackingContext";
import { manageSessionId } from "../../helpers/tracking";
import { hoursTypeMap } from "../../helpers/vacancyHelpers";
// Components
import Button from "../Button/Button";
import ApplicationFormModal from "../SearchResults/ApplicationFormModal";
import ExpiredVacancyAlert from "../SearchResults/ExpiredVacancyAlert";
import VacancyNotFound from "../SearchResults/VacancyNotFound";
import VacancyLoader from "../SearchResults/VacnacyLoader";

// Dynamic imports
const DynamicTFChatBot = dynamic(
  () =>
    import("@e4s-developers/tf-careersite-chatbot").then((mod) => {
      return mod.TFChatBot;
    }),
  {
    ssr: false,
  }
);

const StyledHeadContainer = styled.div`
  padding-top: ${({ imageHeader }) =>
    imageHeader ? "2rem !important" : "0.5rem !important"};
`;

const StyledButtonWrapper = styled.div`
  position: relative;
  flex-wrap: wrap;

  @media (min-width: ${breakpoints.DESKTOP}) {
    flex-wrap: nowrap;
  }
`;

const ImageHeader = styled.div`
  background: #18191c;
  width: 100%;
  left: 0;
  position: absolute;
  top: 0;
`;

const StickyTopBar = styled.div`
  position: sticky;
  height: 0;
  top: 0;
  right: 0;
  z-index: 10;
  width: fit-content;
  margin-left: auto;
`;

const StyledLargeHeaderImage = styled.div`
  position: relative;
  width: 100%;
  height: 400px;

  @media (min-width: ${breakpoints.TABLET}) {
    height: 500px;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    height: 600px;
  }

  img {
    object-fit: cover;
    object-position: center;
  }
`;

const StyledJobInfoOverlay = styled.div`
  position: absolute;
  top: -6rem;
  left: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.95);
  z-index: 5;

  @media (min-width: ${breakpoints.TABLET}) {
    left: 2rem;
    right: 2rem;
    top: -6rem;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    left: 3rem;
    right: 3rem;
    top: -8rem;
  }
`;

const StyledJobInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StyledJobInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const StyledApplyButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledStickyWrapper = styled.div`
  position: -webkit-sticky; /* For Safari */
  position: sticky;
  top: 0;
  z-index: 5;
`;

const currencyIcon = {
  GBP: "£",
  USD: "$",
};

const formatNumber = (number) => {
  if (number == null) return "";
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatHourlyRate = (number) => {
  if (number == null) return "";
  return parseFloat(number).toFixed(2);
};
const getRenumeration = (vacancy) => {
  const currency = currencyIcon[vacancy?.remuneration?.currency] || "£";

  if (vacancy?.remuneration?.description?.toUpperCase() === "COMPETITIVE") {
    return (
      <div className="sticky-job-description-value">
        <div className="sticky-job-description-renumeration-label">
          SALARY RANGE
        </div>
        <div className="sticky-job-description-renumeration-value">
          {vacancy?.remuneration?.description}
        </div>
      </div>
    );
  }

  if (
    vacancy?.remuneration?.interval === "HOURLY" &&
    vacancy?.remuneration?.type === "FIXED"
  ) {
    return (
      <div className="sticky-job-description-value">
        <div className="sticky-job-description-renumeration-value">
          {currency}
          {formatHourlyRate(vacancy?.remuneration?.ranges[0].value)}
        </div>
        <div className="sticky-job-description-renumeration-description">
          PER HOUR
        </div>
      </div>
    );
  }

  if (
    vacancy?.remuneration?.interval === "HOURLY" &&
    vacancy?.remuneration?.type === "RANGE"
  ) {
    return (
      <div className="sticky-job-description-value">
        <div className="sticky-job-description-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)} - {currency}
          {formatNumber(vacancy?.remuneration?.ranges[1].value)}
        </div>
        <div className="sticky-job-description-renumeration-description">
          PER HOUR
        </div>
      </div>
    );
  }

  if (
    vacancy?.remuneration?.interval === "YEARLY" &&
    vacancy?.remuneration?.type === "FIXED"
  ) {
    return (
      <div className="sticky-job-description-value">
        <div className="sticky-job-description-renumeration-label">
          SALARY UP TO
        </div>
        <div className="sticky-job-description-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)}
        </div>
      </div>
    );
  }
  if (
    vacancy?.remuneration?.interval === "YEARLY" &&
    vacancy?.remuneration?.type === "RANGE"
  ) {
    return (
      <div className="sticky-job-description-value">
        <div className="sticky-job-description-renumeration-label">
          SALARY RANGE
        </div>
        <div className="sticky-job-description-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)} - {currency}
          {formatNumber(vacancy?.remuneration?.ranges[1].value)}
        </div>
      </div>
    );
  }

  return null;
};

function StickyJobDescriptionSingleJob(props) {
  const {
    applyButtonPosition,
    modalMode,
    imageHeader,
    thumbnailUrl,
    largeHeader,
  } = props;
  const { sendTrackingEvent } = useAnalytics();
  const [showInteractiveApp, setShowInteractiveApp] = useState(false);
  const params = useParams();
  const searchParams = useSearchParams();
  // In Pages Router, router.query contains both path params and query params
  const { trackingStore } = useStores();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };
  const { refererQueryString } = trackingStore;
  const utm = getUTMFromParams(refererQueryString);

  const smartApply = combindedParms["smart-apply"];
  const {
    formHeaderBackgroundColor,
    gallery,
    additionalConfig,
    applyButtonClassName,
    applyButtonHeight = "3.5rem",
    applyButtonText,
    applyButtonWidth,
    applyButtonTextColour,
    applyButtonBackgroundColour,
    applyButtonBackgroundHoverColour,
    thumbnail,
  } = useContext(ComponentContext);
  const { configStore, modalStore, vacancyStore, rolesStore } = useStores();
  const { config } = configStore;
  const { openModal } = modalStore;
  const { selectedVacancy, isVacancyLoading } = vacancyStore;

  const formHeaderTextColor =
    config?.applicationFormConfig?.textColor || "#FFFFFF";
  const backgroundColor = config?.applicationFormConfig?.backgroundColor;

  const [isVacancyActive, setIsVacancyActive] = useState(true);
  const isInteractiveApply = smartApply === "true";
  const hasInteractiveApplyForm = Boolean(
    selectedVacancy?.applicationDetail?.appId
  );

  useEffect(() => {
    if (!isEmpty(selectedVacancy)) {
      const isVacancyClosed = selectedVacancy.status === "CLOSED";
      setIsVacancyActive(!isVacancyClosed);
    }
  }, [selectedVacancy]);

  const buttonClassNames = classNames(
    "px-5 text-nowrap text-uppercase",
    {
      "w-100 my-4": isMobile,
    },
    applyButtonClassName
  );

  const applyButton = (
    <Button
      className={buttonClassNames}
      maxHeight={applyButtonHeight}
      height={applyButtonHeight}
      width={applyButtonWidth}
      buttonTextColour={applyButtonTextColour}
      buttonColour={applyButtonBackgroundColour}
      buttonHoverColour={applyButtonBackgroundHoverColour}
      onClick={() => {
        const jobId = selectedVacancy.vacancyId || selectedVacancy.id;
        sendTrackingEvent("APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        openModal({
          content: (
            <ApplicationFormModal
              formHeaderBackgroundColor={formHeaderBackgroundColor}
              thumbnailUrl={thumbnailUrl}
            />
          ),
          fullscreen: isMobile,
        });
      }}
    >
      {applyButtonText}
    </Button>
  );
  const interactiveApplyButton = (
    <Button
      className={buttonClassNames}
      id="vacancy-smart-apply-button"
      maxHeight={applyButtonHeight}
      height={applyButtonHeight}
      width={applyButtonWidth}
      onClick={() => {
        const jobId = selectedVacancy.vacancyId || selectedVacancy.id;
        sendTrackingEvent("SMART_APPLY_BUTTON_CLICK", jobId, {
          vacancyId: jobId,
        });

        setShowInteractiveApp(!showInteractiveApp);
      }}
    >
      <span style={{ alignItems: "center", display: "flex" }}>
        <svg
          className="icon"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{
            height: "24px",
            marginRight: "10px",
            width: "24px",
          }}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Smart Apply
      </span>
    </Button>
  );

  if (isVacancyLoading) {
    return <VacancyLoader />;
  }

  if (isEmpty(selectedVacancy)) {
    return <VacancyNotFound />;
  }

  // Find matching role from rolesStore based on vacancy category
  const vacancyRole = rolesStore.roles.find(
    (role) => role.roleName === selectedVacancy.category
  );

  // Determine thumbnail URL with fallback logic
  const headerThumbnailUrl =
    vacancyRole?.thumbnail?.url || thumbnail?.url || gallery?.[0]?.url;

  const largeHeaderImage = headerThumbnailUrl;

  return (
    <div className={`position-relative ${largeHeader ? "w-100" : ""}`}>
      {isVacancyActive && additionalConfig?.hasStickyApplyButton && (
        <StickyTopBar className="sticky-apply-button">
          {applyButton}
        </StickyTopBar>
      )}
      {!isVacancyActive && <ExpiredVacancyAlert />}

      {largeHeaderImage && (
        <>
          <StyledLargeHeaderImage
            className={`sticky-job-description-hero-image ${selectedVacancy?.category
              ?.replace(/[\s&,]/g, "")
              .toLowerCase()} ${selectedVacancy?.location?.city
              ?.replace(/[\s&,]/g, "")
              .toLowerCase()}`}
          >
            <Image
              alt={selectedVacancy?.title || "Job vacancy"}
              layout="fill"
              objectFit="cover"
              src={largeHeaderImage}
              priority
            />
          </StyledLargeHeaderImage>

          <StyledStickyWrapper className="sticky-job-description-wrapper">
            <StyledJobInfoOverlay className="sticky-job-description-card">
              <StyledJobInfoGrid className="sticky-job-description-info-grid">
                <StyledJobInfoItem className="sticky-job-description-location">
                  <div className="sticky-job-description-value">
                    {selectedVacancy?.location?.city}
                    {selectedVacancy?.location?.county &&
                      `, ${selectedVacancy.location.county}`}
                  </div>
                </StyledJobInfoItem>
                <StyledJobInfoItem className="sticky-job-description-title">
                  <div className="sticky-job-description-value">
                    {selectedVacancy?.title}
                  </div>
                </StyledJobInfoItem>
                <StyledJobInfoItem className="sticky-job-description-type">
                  <div className="sticky-job-description-value">
                    {hoursTypeMap[selectedVacancy?.hoursType] || "Full-Time"}
                  </div>
                </StyledJobInfoItem>
                <StyledJobInfoItem className="sticky-job-description-salary">
                  {getRenumeration(selectedVacancy)}
                </StyledJobInfoItem>
              </StyledJobInfoGrid>
              <StyledApplyButtonContainer className="sticky-job-description-apply-container">
                {isVacancyActive && applyButton}
              </StyledApplyButtonContainer>
            </StyledJobInfoOverlay>
          </StyledStickyWrapper>
        </>
      )}
      <StyledHeadContainer
        className="py-4 w-100"
        imageHeader={imageHeader}
        title={selectedVacancy?.title}
      >
        {imageHeader && <ImageHeader id="vacancy-header-background" />}

        {modalMode && isVacancyActive && applyButton}
        <div className="d-flex flex-column flex-md-row gap-4">
          <div
            className="mb-5 col-12 col-md-8"
            dangerouslySetInnerHTML={{ __html: selectedVacancy.description }}
          />
          <div
            className={`mb-5 col-12 col-md-4 ${
              selectedVacancy?.location?.city
                ?.replace(/[\s&,]/g, "")
                .toLowerCase() || ""
            } location-image-container`}
          >
            {/* Location data will be populated via CSS */}
          </div>
        </div>
        <StyledButtonWrapper className="d-flex gap-3">
          {selectedVacancy.description && isVacancyActive && (
            <div
              className={`d-flex justify-content-${applyButtonPosition}`}
              id="vacancy-apply-button"
            >
              {applyButton}
            </div>
          )}
          {(config?.allowInteractiveApplications || isInteractiveApply) &&
            isVacancyActive &&
            hasInteractiveApplyForm &&
            interactiveApplyButton}
        </StyledButtonWrapper>
      </StyledHeadContainer>
      {showInteractiveApp && (
        <DynamicTFChatBot
          formId={selectedVacancy?.applicationDetail?.appId}
          sId={manageSessionId()}
          onClose={() => setShowInteractiveApp(false)}
          show={showInteractiveApp}
          tenant={selectedVacancy.tenant}
          vacancyTitle={selectedVacancy.title || selectedVacancy.jobTitle}
          style={{
            headerBackgroundColour:
              backgroundColor || formHeaderBackgroundColor,
            headerTextColour: formHeaderTextColor,
            headerLogo: selectedVacancy?.company?.logoUrlFull,
          }}
          utm={utm}
        />
      )}
    </div>
  );
}

StickyJobDescriptionSingleJob.defaultProps = {
  applyButtonPosition: "end",
  imageHeader: false,
  modalMode: false,
  thumbnailUrl: undefined,
  largeHeader: false,
};

StickyJobDescriptionSingleJob.propTypes = {
  applyButtonPosition: PropTypes.string,
  imageHeader: PropTypes.bool,
  modalMode: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
  largeHeader: PropTypes.bool,
};

export default observer(StickyJobDescriptionSingleJob);
